import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';

// project import
import MainLayout from 'layout/MainLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import FreeUserGuard from '../utils/route-guard/FreeUserGuard';
import PaidUserGuard from '../utils/route-guard/PaidUserGuard';
import AnalyzerGuard from '../utils/route-guard/AnalyzerGuard';
import ErrorBoundary from 'components/ErrorBoundary';
import DashboardPageSkeleton from 'sections/dashboard/components/loading-skeleton/DashboardPageSkeleton';
import AccountsDashboardPageSkeleton from 'sections/dashboard/components/loading-skeleton/AccountsDashboardPageSkeleton';
import AllTradesPageSkeleton from 'sections/dashboard/components/loading-skeleton/AllTradesPageSkeleton';
import CopyTradingDashboardPageSkeleton from 'sections/dashboard/components/loading-skeleton/CopyTradingDashboardPageSkeleton';
import CopierTemplatesPageSkeleton from 'sections/dashboard/components/loading-skeleton/CopierTemplatesPageSkeleton';
import CopierLogsPageSkeleton from 'sections/dashboard/components/loading-skeleton/CopierLogsPageSkeleton';
import SentimentsPageSkeleton from 'sections/dashboard/components/loading-skeleton/SentimentsPageSkeleton';
import IntegrationPageSkeleton from 'sections/dashboard/components/loading-skeleton/IntegrationsSkeleton';
import EconomicCalendarPageSkeleton from 'sections/dashboard/components/loading-skeleton/EconomicCalendarPageSkeleton';
import SubscriptionsPageSkeleton from 'sections/dashboard/components/loading-skeleton/SubscriptionsPageSkeleton';
import SettingsPageSkeleton from 'sections/dashboard/components/loading-skeleton/SettingsPageSkeleton';

// const ProfilePage = Loadable(lazy(() => import('pages/profile/ProfilePage')));
// const ProfileViewSection = Loadable(lazy(() => import('sections/profile/ProfileViewSection')));
// const ProfileSettingsSection = Loadable(lazy(() => import('sections/profile/ProfileSettingsSection')));

const UserAccountSettingsPage = lazy(() => import('pages/account-settings/AccountSettingsPage'));
const PersonalTabSection = Loadable(lazy(() => import('sections/account-settings/tabs/PersonalTabSection')));
// const BillingPage = Loadable(lazy(() => import('pages/account-settings/BillingPage')));
const ChangePasswordTabSection = Loadable(lazy(() => import('sections/account-settings/tabs/ChangePasswordTabSection')));
const SettingsTabSection = Loadable(lazy(() => import('sections/account-settings/tabs/SettingsTabSection')));
const MarketNewsPage = Loadable(lazy(() => import('pages/trading-tools/MarketNewsPage')));

const DashboardPage = Loadable(lazy(() => import('pages/dashboard/DashboardPage')));
const AccountsDashboardPage = Loadable(lazy(() => import('pages/accounts/AccountsDashboardPage')));
const AccountAnalyticsPage = Loadable(lazy(() => import('pages/accounts/AccountAnalyticsPage')));
const TradeListingPage = lazy(() => import('pages/trade/TradeListingPage'));
const EconomicCalendarPage = lazy(() => import('pages/economic-calendar/EconomicCalendarPage'));

const AccountSettingsPage = Loadable(lazy(() => import('pages/accounts/AccountSettingsPage')));
const ProfileOverviewSection = Loadable(lazy(() => import('sections/accounts/settings/ProfileOverviewSection')));
const AccountManagementSection = Loadable(lazy(() => import('sections/accounts/settings/AccountManagementSection')));
const EquityProtectorSection = Loadable(lazy(() => import('sections/accounts/settings/EquityProtectorSection')));
const TradingSymbolSection = Loadable(lazy(() => import('sections/accounts/settings/TradingSymbolsSection')));

const MapSymbolsPage = Loadable(lazy(() => import('pages/trade-copier/MapSymbolsPage')));
const CopyTradingDashboardPage = lazy(() => import('pages/trade-copier/CopyTradingDashboardPage'));
const CopierLogsPage = lazy(() => import('pages/trade-copier/CopierLogsPage'));
const CopierTemplatesPage = lazy(() => import('pages/trade-copier/CopierTemplatesPage'));

const IntegrationPage = lazy(() => import('pages/integration/IntegrationPage'));
const EmailIntegrationPage = Loadable(lazy(() => import('pages/integration/EmailIntegrationPage')));
const EmailConfigurationSection = Loadable(lazy(() => import('sections/integration/email/EmailConfigurationSection')));
const NotificationSection = Loadable(lazy(() => import('sections/integration/email/NotificationSection')));

const DedicatedEnvironmentPage = Loadable(lazy(() => import('pages/integration/DedicatedEnvironmentPage')));
const DedicatedEnvConfigurationSection = Loadable(
  lazy(() => import('sections/integration/dedicated-env/DedicatedEnvConfigurationSection'))
);
const DedicatedEnvAccountSection = Loadable(lazy(() => import('sections/integration/dedicated-env/DedicatedEnvAccountSection')));

const SubscriptionsPage = lazy(() => import('pages/account-settings/SubscriptionsPage'));
const SubscriptionsTabSection = Loadable(lazy(() => import('sections/account-settings/subscriptions/SubscriptionsTabSection')));
const PaymentMethodTabSection = Loadable(lazy(() => import('sections/account-settings/subscriptions/PaymentMethodTabSection')));
const InvoiceTabSection = Loadable(lazy(() => import('sections/account-settings/subscriptions/InvoiceTabSection')));

const UnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction')));

const AnalyzerPerformancePage = Loadable(lazy(() => import('pages/analyzer/AnalyzerPerformancePage')));
const AnalyzerPortfolioPage = Loadable(lazy(() => import('pages/analyzer/AnalyzerPortfolioPage')));
const AnalyzerAnalysisHubPage = Loadable(lazy(() => import('pages/analyzer/AnalyzerAnalysisHubPage')));
const SentimentsPage = lazy(() => import('pages/sentiments/SentimentsPage'));
const PalettePreview = Loadable(lazy(() => import('pages/PalettePreview')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  errorElement: <ErrorBoundary />,
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        {
          path: 'dashboard',
          element: (
            <Suspense fallback={<DashboardPageSkeleton />}>
              <DashboardPage />
            </Suspense>
          )
        },
        {
          path: 'economic-calendar',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<EconomicCalendarPageSkeleton />}>
                <EconomicCalendarPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts',
          element: (
            <Suspense fallback={<AccountsDashboardPageSkeleton />}>
              <AccountsDashboardPage />
            </Suspense>
          )
        },
        {
          path: 'account-trades',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<AllTradesPageSkeleton />}>
                <TradeListingPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts/:reAuth',
          element: <AccountsDashboardPage />
        },
        {
          path: 'accounts/analytics/:id',
          element: (
            <PaidUserGuard>
              <AccountAnalyticsPage />
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts/settings/:id',
          element: (
            <PaidUserGuard>
              <AccountSettingsPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <ProfileOverviewSection />
            },
            {
              path: 'overview',
              element: <ProfileOverviewSection />
            },
            {
              path: 'account-management',
              element: <AccountManagementSection />
            },
            {
              path: 'equity-protector',
              element: <EquityProtectorSection />
            },
            {
              path: 'trading-symbols',
              element: <TradingSymbolSection />
            }
          ]
        },
        {
          path: 'trade-copier/copier-settings',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopyTradingDashboardPageSkeleton />}>
                <CopyTradingDashboardPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/copier-logs',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopierLogsPageSkeleton />}>
                <CopierLogsPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/templates',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopierTemplatesPageSkeleton />}>
                <CopierTemplatesPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/copier-settings/map-symbols',
          element: (
            <PaidUserGuard>
              <MapSymbolsPage />
            </PaidUserGuard>
          )
        },
        {
          path: 'integration',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<IntegrationPageSkeleton />}>
                <IntegrationPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'integration/email',
          element: (
            <PaidUserGuard>
              <EmailIntegrationPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <EmailConfigurationSection />
            },
            {
              path: 'configuration',
              element: <EmailConfigurationSection />
            },
            {
              path: 'settings',
              element: <NotificationSection />
            }
          ]
        },
        {
          path: 'integration/dedicated-environment',
          element: (
            <PaidUserGuard>
              <DedicatedEnvironmentPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'env-configuration',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'account-configuration',
              element: <DedicatedEnvAccountSection />
            }
          ]
        },
        {
          path: 'analyzer/performance',
          element: <AnalyzerGuard><AnalyzerPerformancePage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/portfolio',
          element: <AnalyzerGuard><AnalyzerPortfolioPage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/analysis-hub',
          element: <AnalyzerGuard><AnalyzerAnalysisHubPage /></AnalyzerGuard>
        },
        {
          path: 'sentiments',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<SentimentsPageSkeleton />}>
                <SentimentsPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        { path: 'color-palette', element: <PalettePreview /> },
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        {
          path: 'account-settings/billing',
          element: (
            <Suspense fallback={<SubscriptionsPageSkeleton />}>
              <SubscriptionsPage />
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: <SubscriptionsTabSection />
            },
            {
              path: 'subscriptions',
              element: <SubscriptionsTabSection />
            },
            {
              path: 'payment-methods',
              element: <PaymentMethodTabSection />
            },
            {
              path: 'invoices',
              element: <InvoiceTabSection />
            }
          ]
        },
        {
          path: 'account-settings',
          element: (
            <Suspense fallback={<SettingsPageSkeleton />}>
              <UserAccountSettingsPage />
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: <PersonalTabSection />
            },
            {
              path: 'personal',
              element: <PersonalTabSection />
            },
            {
              path: 'change-password',
              element: <ChangePasswordTabSection />
            },
            {
              path: 'edit-settings',
              element: <SettingsTabSection />
            }
          ]
        },
        {
          path: 'analyzer/performance',
          element: <AnalyzerGuard><AnalyzerPerformancePage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/portfolio',
          element: <AnalyzerGuard><AnalyzerPortfolioPage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/analysis-hub',
          element: <AnalyzerGuard><AnalyzerAnalysisHubPage /></AnalyzerGuard>
        },
        {
          path: 'tools/market-news',
          element: <MarketNewsPage />,
          children: []
        },
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    }
  ]
};

export const MainRoutesBeta = {
  path: '/',
  errorElement: <ErrorBoundary />,
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        {
          path: 'dashboard',
          element: <DashboardPage />
        },
        {
          path: 'economic-calendar',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<EconomicCalendarPageSkeleton />}>
                <EconomicCalendarPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts',
          element: <AccountsDashboardPage />
        },
        {
          path: 'account-trades',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<AllTradesPageSkeleton />}>
                <TradeListingPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts/:reAuth',
          element: <AccountsDashboardPage />
        },
        {
          path: 'accounts/analytics/:id',
          element: (
            <PaidUserGuard>
              <AccountAnalyticsPage />
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts/settings/:id',
          element: (
            <PaidUserGuard>
              <AccountSettingsPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <ProfileOverviewSection />
            },
            {
              path: 'overview',
              element: <ProfileOverviewSection />
            },
            {
              path: 'account-management',
              element: <AccountManagementSection />
            },
            {
              path: 'equity-protector',
              element: <EquityProtectorSection />
            },
            {
              path: 'trading-symbols',
              element: <TradingSymbolSection />
            }
          ]
        },
        {
          path: 'trade-copier/copier-settings',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopyTradingDashboardPageSkeleton />}>
                <CopyTradingDashboardPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/copier-logs',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopierLogsPageSkeleton />}>
                <CopierLogsPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/templates',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopierTemplatesPageSkeleton />}>
                <CopierTemplatesPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/copier-settings/map-symbols',
          element: (
            <PaidUserGuard>
              <MapSymbolsPage />
            </PaidUserGuard>
          )
        },
        {
          path: 'integration',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<IntegrationPageSkeleton />}>
                <IntegrationPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'integration/email',
          element: (
            <PaidUserGuard>
              <EmailIntegrationPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <EmailConfigurationSection />
            },
            {
              path: 'configuration',
              element: <EmailConfigurationSection />
            },
            {
              path: 'settings',
              element: <NotificationSection />
            }
          ]
        },
        {
          path: 'integration/dedicated-environment',
          element: (
            <PaidUserGuard>
              <DedicatedEnvironmentPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'env-configuration',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'account-configuration',
              element: <DedicatedEnvAccountSection />
            }
          ]
        },
        {
          path: 'analyzer/performance',
          element: <AnalyzerGuard><AnalyzerPerformancePage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/portfolio',
          element: <AnalyzerGuard><AnalyzerPortfolioPage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/analysis-hub',
          element: <AnalyzerGuard><AnalyzerAnalysisHubPage /></AnalyzerGuard>
        },
        {
          path: 'sentiments',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<SentimentsPageSkeleton />}>
                <SentimentsPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        { path: 'color-palette', element: <PalettePreview /> },
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    },
    {
      path: '/',
      element: (
        <FreeUserGuard>
          <MainLayout />
        </FreeUserGuard>
      ),
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        {
          path: 'account-settings/billing',
          element: (
            <Suspense fallback={<SubscriptionsPageSkeleton />}>
              <SubscriptionsPage />
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: <SubscriptionsTabSection />
            },
            {
              path: 'subscriptions',
              element: <SubscriptionsTabSection />
            },
            {
              path: 'payment-methods',
              element: <PaymentMethodTabSection />
            },
            {
              path: 'invoices',
              element: <InvoiceTabSection />
            }
          ]
        },
        {
          path: 'account-settings',
          element: (
            <Suspense fallback={<SettingsPageSkeleton />}>
              <UserAccountSettingsPage />
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: <PersonalTabSection />
            },
            {
              path: 'personal',
              element: <PersonalTabSection />
            },
            {
              path: 'change-password',
              element: <ChangePasswordTabSection />
            },
            {
              path: 'edit-settings',
              element: <SettingsTabSection />
            }
          ]
        },
        {
          path: 'analyzer/performance',
          element: <AnalyzerGuard><AnalyzerPerformancePage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/portfolio',
          element: <AnalyzerGuard><AnalyzerPortfolioPage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/analysis-hub',
          element: <AnalyzerGuard><AnalyzerAnalysisHubPage /></AnalyzerGuard>
        },
        {
          path: 'tools/market-news',
          element: <MarketNewsPage />,
          children: []
        },
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    }
  ]
};

export const MainRoutesAdmin = {
  path: '/',
  errorElement: <ErrorBoundary />,
  children: [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        {
          path: 'dashboard',
          element: <DashboardPage />
        },
        {
          path: 'economic-calendar',
          element: (
            <Suspense fallback={<EconomicCalendarPageSkeleton />}>
              <EconomicCalendarPage />
            </Suspense>
          )
        },
        {
          path: 'accounts',
          element: <AccountsDashboardPage />
        },
        {
          path: 'account-trades',
          element: (
            <Suspense fallback={<AllTradesPageSkeleton />}>
              <TradeListingPage />
            </Suspense>
          )
        },
        {
          path: 'accounts/:reAuth',
          element: <AccountsDashboardPage />
        },
        {
          path: 'accounts/analytics/:id',
          element: <AccountAnalyticsPage />
        },
        {
          path: 'accounts/settings/:id',
          element: <AccountSettingsPage />,
          children: [
            {
              path: '',
              element: <ProfileOverviewSection />
            },
            {
              path: 'overview',
              element: <ProfileOverviewSection />
            },
            {
              path: 'account-management',
              element: <AccountManagementSection />
            },
            {
              path: 'equity-protector',
              element: <EquityProtectorSection />
            },
            {
              path: 'trading-symbols',
              element: <TradingSymbolSection />
            }
          ]
        },
        {
          path: 'trade-copier/copier-settings',
          element: (
            <Suspense fallback={<CopyTradingDashboardPageSkeleton />}>
              <CopyTradingDashboardPage />
            </Suspense>
          )
        },
        {
          path: 'trade-copier/copier-logs',
          element: (
            <Suspense fallback={<CopierLogsPageSkeleton />}>
              <CopierLogsPage />
            </Suspense>
          )
        },
        {
          path: 'trade-copier/templates',
          element: (
            <Suspense fallback={<CopierTemplatesPageSkeleton />}>
              <CopierTemplatesPage />
            </Suspense>
          )
        },
        {
          path: 'trade-copier/copier-settings/map-symbols',
          element: <MapSymbolsPage />
        },
        {
          path: 'integration',
          element: (
            <Suspense fallback={<IntegrationPageSkeleton />}>
              <IntegrationPage />
            </Suspense>
          )
        },
        {
          path: 'integration/email',
          element: <EmailIntegrationPage />,
          children: [
            {
              path: '',
              element: <EmailConfigurationSection />
            },
            {
              path: 'configuration',
              element: <EmailConfigurationSection />
            },
            {
              path: 'settings',
              element: <NotificationSection />
            }
          ]
        },
        {
          path: 'integration/dedicated-environment',
          element: <DedicatedEnvironmentPage />,
          children: [
            {
              path: '',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'env-configuration',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'account-configuration',
              element: <DedicatedEnvAccountSection />
            }
          ]
        },
        {
          path: 'analyzer/performance',
          element: <AnalyzerGuard><AnalyzerPerformancePage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/portfolio',
          element: <AnalyzerGuard><AnalyzerPortfolioPage /></AnalyzerGuard>
        },
        {
          path: 'analyzer/analysis-hub',
          element: <AnalyzerGuard><AnalyzerAnalysisHubPage /></AnalyzerGuard>
        },
        {
          path: 'sentiments',
          element: (
            <Suspense fallback={<SentimentsPageSkeleton />}>
              <SentimentsPage />
            </Suspense>
          )
        },
        { path: 'color-palette', element: <PalettePreview /> },
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    },
    {
      path: '/',
      element: (
        <FreeUserGuard>
          <MainLayout />
        </FreeUserGuard>
      ),
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        {
          path: 'account-settings/billing',
          element: (
            <Suspense fallback={<SubscriptionsPageSkeleton />}>
              <SubscriptionsPage />
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: <SubscriptionsTabSection />
            },
            {
              path: 'subscriptions',
              element: <SubscriptionsTabSection />
            },
            {
              path: 'payment-methods',
              element: <PaymentMethodTabSection />
            },
            {
              path: 'invoices',
              element: <InvoiceTabSection />
            }
          ]
        },
        {
          path: 'account-settings',
          element: (
            <Suspense fallback={<SettingsPageSkeleton />}>
              <UserAccountSettingsPage />
            </Suspense>
          ),
          children: [
            {
              path: '',
              element: <PersonalTabSection />
            },
            {
              path: 'personal',
              element: <PersonalTabSection />
            },
            {
              path: 'change-password',
              element: <ChangePasswordTabSection />
            },
            {
              path: 'edit-settings',
              element: <SettingsTabSection />
            }
          ]
        },
        {
          path: 'analyzer',
          element: <AnalyzerGuard><AnalyzerPerformancePage /></AnalyzerGuard>,
          children: [
            {
              path: 'performance',
              element: <AnalyzerGuard><AnalyzerPerformancePage /></AnalyzerGuard>
            },
            {
              path: 'portfolio',
              element: <AnalyzerGuard><AnalyzerPortfolioPage /></AnalyzerGuard>
            }
          ]
        },
        {
          path: 'tools/market-news',
          element: <MarketNewsPage />,
          children: []
        },
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    }
  ]
};

export default MainRoutes;
